import { QueryClient, QueryFunction } from "@tanstack/react-query";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

let _csrfToken: string | null = null;

async function getCsrfToken(): Promise<string> {
  if (_csrfToken) return _csrfToken;
  const res = await fetch("/api/csrf-token", { credentials: "include" });
  const data = await res.json();
  _csrfToken = data.csrfToken as string;
  return _csrfToken;
}

export function clearCsrfToken() {
  _csrfToken = null;
}

// Shared error-message parser used by throwIfResNotOk and exported for callers (e.g. admin.tsx)
// that surface errors in UI strings rather than thrown exceptions.
export async function parseApiError(res: Response, fallback: string): Promise<string> {
  const statusSuffix = `${res.status}${res.statusText ? ` ${res.statusText}` : ""}`;
  let text = "";
  try {
    text = await res.text();
  } catch {
    return `${fallback} (${statusSuffix})`;
  }
  if (text) {
    try {
      const body = JSON.parse(text);
      const msg = body?.error?.message ?? body?.message;
      if (typeof msg === "string" && msg.trim()) return msg;
    } catch {
      const snippet = text
        .replace(/<[^>]+>/g, " ")
        .split("\n")
        .map((l: string) => l.trim())
        .find(Boolean);
      if (snippet) return `${fallback} (${statusSuffix}): ${snippet.slice(0, 200)}`;
    }
  }
  return `${fallback} (${statusSuffix})`;
}

async function throwIfResNotOk(res: Response): Promise<void> {
  if (!res.ok) {
    let message = res.statusText || String(res.status);
    let code: string | undefined;
    try {
      const text = await res.text();
      if (text) {
        try {
          const body = JSON.parse(text);
          code = body?.error?.code ?? body?.code;
          const msg = body?.error?.message ?? body?.message;
          if (typeof msg === "string" && msg.trim()) message = msg;
        } catch {
          // non-JSON body — use as-is only if it looks like plain text
          if (!text.startsWith("{") && !text.startsWith("[")) message = text;
        }
      }
    } catch {
      // body unreadable — keep statusText
    }
    throw new ApiError(message, res.status, code);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const makeRequest = async (isRetry: boolean): Promise<Response> => {
    const headers: Record<string, string> = data
      ? { "Content-Type": "application/json" }
      : {};

    if (method.toUpperCase() !== "GET") {
      headers["x-csrf-token"] = await getCsrfToken();
    }

    const res = await fetch(url, {
      method,
      headers,
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include",
    });

    // Auto-recover from a stale CSRF token — retry exactly once, only for CSRF_INVALID
    if (!isRetry && res.status === 403 && method.toUpperCase() !== "GET") {
      try {
        const body = await res.clone().json();
        if ((body?.error?.code ?? body?.code) === "CSRF_INVALID") {
          clearCsrfToken();
          return makeRequest(true);
        }
      } catch {
        // non-JSON 403 — fall through to normal error path
      }
    }

    await throwIfResNotOk(res);
    return res;
  };

  return makeRequest(false);
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey.length > 1
      ? `${queryKey[0]}/${queryKey.slice(1).join("/")}`
      : (queryKey[0] as string);
    const res = await fetch(url, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    if (res.status === 401) {
      return null;
    }

    if (res.status === 402) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "returnNull" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
